<script lang="ts">
  import { map } from 'lodash-es'
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { normalizeStringWithFolding } from '#app/lib/strings'
  import InputWithClearButton from '#components/input_with_clear_button.svelte'
  import Modal from '#components/modal.svelte'
  import Spinner from '#components/spinner.svelte'
  import ShelfInfo from '#inventory/components/shelf_info.svelte'
  import type { ShelfId } from '#server/types/shelf'
  import ShelfEditor from '#shelves/components/shelf_editor.svelte'
  import { getShelvesByOwner, shelfMatchesFilter, type SerializedShelf } from '#shelves/lib/shelves'
  import { I18n } from '#user/lib/i18n'
  import { mainUser } from '#user/lib/main_user'

  let {
    shelvesIds = $bindable(),
    showDescription = false,
  }: {
    shelvesIds: ShelfId[]
    showDescription?: boolean
  } = $props()

  let userShelves: SerializedShelf[] = $state([])
  let flash: FlashState | undefined = $state()
  let showShelfCreator: boolean | undefined = $state()

  const waitForShelves = getShelvesByOwner(mainUser?._id)
    .then(res => {
      userShelves = res
      const userShelvesIds = map(userShelves, '_id')
      // Filter-out any shelf that might have been deleted
      // but passed as it was saved by `getLastShelves` (possibly useless now that `getLastShelves` has been removed)
      shelvesIds = shelvesIds?.filter(id => userShelvesIds.includes(id)) || []
    })
    .catch(err => flash = err)

  let shelvesFilter: string = $state('')
  const normalizedShelvesFilter = $derived(normalizeStringWithFolding(shelvesFilter))
  const filteredDisplayedShelvesIds = $derived.by(() => {
    const ids = userShelves
      .filter(shelf => shelfMatchesFilter(shelf, normalizedShelvesFilter))
      .map(shelf => shelf._id)
    return new Set(ids)
  })

  function onShelfEditorDone (newShelf: SerializedShelf) {
    showShelfCreator = false
    userShelves.push(newShelf)
    shelvesIds.push(newShelf._id)
  }
</script>

<fieldset>
  {#await waitForShelves}
    <Spinner /><span class="title">{I18n('shelves')}</span>
  {:then}
    {#if userShelves.length > 0}
      <legend>
        <p class="title">{I18n('shelves')}</p>
        {#if showDescription}
          <p class="description">{I18n('shelves_importer_description')}</p>
        {/if}
        <!-- i18n suggestion: "shelves_importer_description": "Add the selected books to your shelf(ves)" -->
      </legend>
      {#if userShelves.length > 5}
        <InputWithClearButton
          bind:value={shelvesFilter}
          placeholder="Filter shelves…"
          count={filteredDisplayedShelvesIds.size}
          total={userShelves.length}
        />
      {/if}
      <ul>
        {#each userShelves as shelf (shelf._id)}
          {@const shown = filteredDisplayedShelvesIds.has(shelf._id)}
          <li>
            <label title={I18n('select_shelf')} class="shelf" class:shown>
              <input type="checkbox" bind:group={shelvesIds} value={shelf._id} />
              <ShelfInfo {shelf} />
            </label>
          </li>
        {/each}
      </ul>
    {/if}
    <button
      class="create tiny-button"
      aria-haspopup="dialog"
      onclick={() => showShelfCreator = true}
    >
      {@html icon('plus')}{I18n('create shelf')}
    </button>
  {/await}
</fieldset>

<Flash state={flash} />

{#if showShelfCreator}
  <Modal onCloseModal={() => showShelfCreator = false}>
    <ShelfEditor {onShelfEditorDone} />
  </Modal>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  @import "inventory/scss/shelves_selectors";

  fieldset{
    :global(.input-with-clear-button){
      margin: 0.5rem 0;
    }
  }
  ul{
    max-height: max(50vh, 25rem);
    overflow-y: auto;
  }
  .shelf{
    &:not(.shown){
      display: none;
    }
  }
  .create{
    margin: 0.5rem 0;
  }
</style>
